export interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: TelegramUser;
    auth_date?: number;
    hash: string;
  };
  platform: string;
  version: string;
  ready: () => void;
  expand: () => void;
}

/**
 * Открыто ли приложение внутри Telegram Mini App.
 *
 * Скрипт `telegram-web-app.js` лежит в `public/` (self-host, без telegram.org)
 * и создаёт `window.Telegram.WebApp` ВЕЗДЕ — включая обычный браузер.
 * Поэтому простого `!!window.Telegram.WebApp` мало: вне TG это будет
 * заглушка с пустым `initData` и `platform === 'unknown'`. Считаем «мы
 * в TG», только если есть подписанные данные или известная платформа.
 */
export const isTelegramWebApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tg = (window as any).Telegram?.WebApp;
  if (!tg) return false;

  const hasInitData = typeof tg.initData === 'string' && tg.initData.length > 0;
  const knownPlatform =
    typeof tg.platform === 'string' && tg.platform.length > 0 && tg.platform !== 'unknown';

  return hasInitData || knownPlatform;
};

/**
 * Доступ к WebApp instance или `null`. Сразу вызывает `tg.ready()` —
 * это исторически точка входа в SDK, без него часть API клиента
 * (например, fullscreen) может работать с задержкой.
 */
export const getTelegramWebApp = (): TelegramWebApp | null => {
  if (!isTelegramWebApp()) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tg = (window as any).Telegram?.WebApp as TelegramWebApp | undefined;
  if (!tg) return null;
  tg.ready();
  return tg;
};

/**
 * Подписанные данные пользователя из TG для `POST /auth/telegram/exchange`.
 * Вне TG — пустая строка / `null`.
 */
export const getTelegramAuthData = (): { initData: string; userId: number | null } => {
  const tg = getTelegramWebApp();
  if (!tg) return { initData: '', userId: null };
  return {
    initData: tg.initData,
    userId: tg.initDataUnsafe?.user?.id ?? null
  };
};

/**
 * Открыть внешнюю ссылку (оплата, поддержка и т.п.): `tg.openLink` внутри
 * Mini App (иначе страница откроется в системном браузере поверх приложения),
 * иначе — обычный переход.
 */
export const openExternalUrl = (url: string): void => {
  if (isTelegramWebApp()) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(url);
      return;
    }
  }
  window.location.href = url;
};
